import { OBJECT_SCREEN_TAB_SEARCH_PARAM } from '@components/v2/layout/ObjectScreen/objectScreen.constants';
import * as R from 'ramda';
import LangEnum from '../../lang/LangEnum';
import PATHS from '../../routes/routes.const';
import { getBestTimeFormat } from '../../utils/dates';
import { Locales } from '../translation/messages';
import { Ability } from '../users/ability.types';
import { UserRole } from '../users/roles';
import { Connection, Invite, Paths, StoreOrBrand, StoreOrBrandAddress } from './myNetwork.types';

type GetUserInfoAsReturn = {
  name: string;
  address?: string;
  title?: string;
  time: string;
};

const getAddress = (item: StoreOrBrand) => {
  if (R.isEmpty(item.addresses) || R.isNil(item.addresses)) {
    return undefined;
  }
  const { address } = item.addresses[0];
  return `${address.city} - ${address.state}`;
};

export const getCounterpart = (item: Connection | Invite, isBrand: boolean) =>
  isBrand ? item.store : item.brand;

export const getMyNetworkTabPath = (tab: Paths) =>
  `${PATHS.main.myNetwork.path}?${OBJECT_SCREEN_TAB_SEARCH_PARAM}=${tab}`;

export const getCounterpartLabel = (role: UserRole | undefined): LangEnum =>
  role === 'COMPRADOR' || role === 'ADMIN_LOJA' ? LangEnum.GLOBAL_BRAND : LangEnum.GLOBAL_STORE;

export const getUserInfoAs = (
  inviteSent: Connection | Invite,
  type: 'store' | 'brand',
  locale: Locales,
): GetUserInfoAsReturn => {
  const item = inviteSent[type === 'brand' ? 'store' : 'brand'];

  const time = getBestTimeFormat(new Date(inviteSent.createdAt), new Date(), locale);

  return {
    name: item.name,
    address: getAddress(item),
    title: item.title,
    time,
  };
};

export const getUserInfo = (
  cardType: Connection | Invite,
  path: Paths,
  locale: Locales,
  ability: Ability,
  role?: UserRole,
) => {
  if (!role) {
    return {
      name: 'bloqueado',
      address: 'bloqueado',
      time: 'bloqueado',
      title: 'bloqueado',
    };
  }

  if (path === Paths.INVITES_SUGGESTIONS) {
    switch (role) {
      case 'ADMIN_MARCA':
        return getUserInfoAs(cardType, 'brand', locale);
      case 'VENDEDOR':
        return getUserInfoAs(cardType, 'brand', locale);
      default:
        return ability.can('read', 'suggested-invites')
          ? getUserInfoAs(cardType, 'brand', locale)
          : { name: '-', address: undefined, time: '-', title: undefined };
    }
  }

  switch (role) {
    case 'ADMIN_MARCA':
      return getUserInfoAs(cardType, 'brand', locale);
    case 'VENDEDOR':
      return getUserInfoAs(cardType, 'brand', locale);
    case 'ADMIN_LOJA':
      return getUserInfoAs(cardType, 'store', locale);
    case 'COMPRADOR':
      return getUserInfoAs(cardType, 'store', locale);
    default:
      return ability.can('read', 'invites')
        ? getUserInfoAs(cardType, 'brand', locale)
        : { name: '-', address: undefined, time: '-', title: undefined };
  }
};

export const getCardDescription = (
  isBrand: boolean,
  addresses: StoreOrBrandAddress[],
  title?: string,
) => {
  if (isBrand) {
    const address = addresses.find(({ principal }) => principal);
    return address ? `${address?.address.city}, ${address?.address.state}` : '';
  }
  return title || '';
};

export const canSeeReceived = (ability: Ability) => {
  return ability.can('read', 'received-invites');
};

export const canSeeSuggested = (ability: Ability) => {
  return ability.can('read', 'suggested-invites');
};

export const isRefusedByStore = (invite: Pick<Invite, 'brandStatus' | 'storeStatus'>) =>
  invite.storeStatus === 'IGNORADO' && invite.brandStatus === 'SOLICITADO';

export const isRefusedByBrand = (invite: Pick<Invite, 'brandStatus' | 'storeStatus'>) =>
  invite.storeStatus === 'SOLICITADO' &&
  (invite.brandStatus === 'IGNORADO' || invite.brandStatus === 'RECUSADO_ADMIN');

export const canAcceptReceivedInvite = (
  invite: Pick<Invite, 'brandStatus' | 'storeStatus'>,
  isAdminStore: boolean,
) => !isRefusedByStore(invite) || isAdminStore;
